import React, { useState, useEffect, useRef } from 'react';
import {
    SparklesIcon,
    FilmIcon,
    VideoIcon,
    MusicIcon,
    CheckCircleIcon,
    X as XIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    MousePointerClickIcon,
    KeyIcon
} from 'lucide-react';

interface OnboardingStep {
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    targetId?: string; // data-tour attribute value
    placement?: 'left' | 'right' | 'top' | 'bottom';
}

interface OnboardingGuideProps {
    isProjectActive?: boolean;
    isSettingsOpen?: boolean;
    activeSettingsTab?: 'profile' | 'account' | 'integrations';
    apiKeyPresent?: boolean;
    falKeyPresent?: boolean;
    onCloseSettings?: () => void;
    onSetTab?: (tab: 'conception' | 'editing' | 'export') => void;
    activeTab?: 'conception' | 'editing' | 'export';
}

const steps: OnboardingStep[] = [
    {
        title: "Bienvenue dans Studio Jenial",
        description: "Votre studio de création vidéo boosté par l'IA. Cette version Beta vous donne accès aux outils les plus avancés de création générative. Suivez le guide pour découvrir l'interface.",
        icon: <SparklesIcon className="w-12 h-12" />,
        color: "bg-indigo-500",
        // No target = Centered Modal
    },
    {
        title: "Accès aux Paramètres",
        description: "Commencez par ouvrir vos paramètres ici pour configurer votre environnement de travail.",
        icon: <MousePointerClickIcon className="w-8 h-8" />,
        color: "bg-indigo-600",
        targetId: "settings-trigger",
        placement: "right"
    },
    {
        title: "Configuration des APIs",
        description: "Une fois les paramètres ouverts, allez dans l'onglet Intégrations pour connecter vos clés Google Gemini et Fal.ai.",
        icon: <KeyIcon className="w-8 h-8" />,
        color: "bg-indigo-600",
        targetId: "settings-integrations-tab",
        placement: "right"
    },
    {
        title: "Clé Google Gemini (Veo)",
        description: "C'est ici que vous entrez votre clé Google AI Studio. Si vous n'en avez pas, cliquez sur le lien 'Obtenir une clé gratuite' pour en générer une en quelques secondes.",
        icon: <SparklesIcon className="w-8 h-8" />,
        color: "bg-indigo-500",
        targetId: "gemini-api-input",
        placement: "right"
    },
    {
        title: "Clé Fal.ai (LTX-2)",
        description: "Pour utiliser le modèle LTX-2 haute performance, entrez votre clé Fal.ai ici. Vous pouvez également en obtenir une sur leur tableau de bord via le lien dédié.",
        icon: <VideoIcon className="w-8 h-8" />,
        color: "bg-purple-500",
        targetId: "fal-api-input",
        placement: "right"
    },
    {
        title: "Sauvegarde Google Drive",
        description: "Connectez votre compte Drive pour sécuriser vos projets. Les vidéos HD générées seront automatiquement stockées dans le dossier de votre choix.",
        icon: <CheckCircleIcon className="w-8 h-8" />,
        color: "bg-blue-500",
        targetId: "drive-section",
        placement: "right"
    },
    {
        title: "Assistant de Conception",
        description: "Une fois vos clés configurées, discutez avec l'IA pour créer vos personnages, définir votre vision et générer vos prompts. L'assistant maintient le contexte pour une cohérence parfaite.",
        icon: <SparklesIcon className="w-8 h-8" />,
        color: "bg-purple-500",
        targetId: "prompt-assistant",
        placement: "right"
    },
    {
        title: "Gestionnaires de Contexte",
        description: "Gérez vos Dogmas (styles) et Personnages ici. Les sélections actives influencent directement toutes les générations de l'assistant.",
        icon: <VideoIcon className="w-8 h-8" />,
        color: "bg-pink-500",
        targetId: "context-managers",
        placement: "bottom"
    },
    {
        title: "Séquence & Storyboard",
        description: "Vos prompts générés apparaissent ici. Vous pouvez voir les aperçus, régénérer des images clés (Nano) ou modifier le texte avant de lancer la vidéo.",
        icon: <FilmIcon className="w-8 h-8" />,
        color: "bg-blue-500",
        targetId: "sequence-manager",
        placement: "left"
    },
    {
        title: "Bibliothèque & Montage",
        description: "Retrouvez tous vos plans générés et vos rushes importés ici. Glissez-les dans la timeline (onglet Editing) pour monter votre film final.",
        icon: <MusicIcon className="w-8 h-8" />,
        color: "bg-green-500",
        targetId: "bin-manager",
        placement: "left"
    }
];

export const OnboardingGuide: React.FC<OnboardingGuideProps> = ({
    isProjectActive = false,
    isSettingsOpen = false,
    activeSettingsTab = 'profile',
    activeTab = 'conception',
    apiKeyPresent = false,
    falKeyPresent = false,
    onCloseSettings,
    onSetTab
}) => {
    // Check localStorage immediately to prevent re-triggering
    const hasSeenOnboarding = localStorage.getItem('studio_jenial_onboarding_seen_v2');
    const [isOpen, setIsOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const [hasInitialized, setHasInitialized] = useState(false);
    const padding = 12; // Increased padding around target

    useEffect(() => {
        const hasSeenOnboardingCurrent = localStorage.getItem('studio_jenial_onboarding_seen_v2');

        // If already seen, never show
        if (hasSeenOnboardingCurrent === 'true') {
            return;
        }

        // Only show onboarding if:
        // 1. Haven't already initialized in this mount
        // 2. A project is active (prevents showing with ProjectWelcomeModal)
        if (hasInitialized || !isProjectActive) {
            return;
        }

        // Auto-open first step (Welcome) - only when project becomes active
        const timer = setTimeout(() => {
            setIsOpen(true);
            setHasInitialized(true);
        }, 800);
        return () => clearTimeout(timer);
    }, [hasInitialized, isProjectActive]);

    // === TAB CONTROL & POSITION TRACKING ===
    useEffect(() => {
        if (!isOpen) return;

        const step = steps[currentStep];

        // 1. Handle Automatic Tab Switching
        if (onSetTab) {
            if (currentStep === 9) { // Last step (Bin Manager)
                if (activeTab !== 'editing') onSetTab('editing');
            } else if (currentStep >= 6) { // Middle steps (Assistant, Dogma, Sequence)
                if (activeTab !== 'conception') onSetTab('conception');
            }
        }

        // 2. Track element position
        if (step.targetId) {
            const updatePosition = (shouldScroll = true) => {
                const el = document.querySelector(`[data-tour="${step.targetId}"]`);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    if (rect.width > 0 && rect.height > 0) {
                        setTargetRect(rect);
                        if (shouldScroll) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                        }
                    } else {
                        setTargetRect(null);
                    }
                } else {
                    setTargetRect(null);
                }
            };

            updatePosition(true);
            const handleResize = () => updatePosition(false);
            window.addEventListener('resize', handleResize);
            const interval = setInterval(() => updatePosition(false), 500);

            return () => {
                window.removeEventListener('resize', handleResize);
                clearInterval(interval);
            };
        } else {
            setTargetRect(null);
        }
    }, [currentStep, isOpen, onSetTab, activeTab]);

    // === MISSION AUTO-ADVANCE ===
    useEffect(() => {
        if (!isOpen) return;

        // Step 2: Open Settings
        if (currentStep === 1 && isSettingsOpen) {
            handleNext();
        }
        // Step 3: Integrations Tab
        if (currentStep === 2 && isSettingsOpen && activeSettingsTab === 'integrations') {
            handleNext();
        }
        // Step 4: Gemini Key
        if (currentStep === 3 && apiKeyPresent) {
            handleNext();
        }
        // Step 5: Fal Key -> Close modal before advancing
        if (currentStep === 4 && falKeyPresent) {
            if (onCloseSettings) {
                onCloseSettings();
            }
            // Small delay to let modal close before advancing
            setTimeout(() => {
                setCurrentStep(c => c + 1);
            }, 300);
        }
    }, [isOpen, currentStep, isSettingsOpen, activeSettingsTab, apiKeyPresent, falKeyPresent, onCloseSettings]);

    // === MODAL CONFLICT GUARD ===
    // For steps 5+ (non-settings steps), hide guide if modal is still open
    const isModalConflict = isSettingsOpen && currentStep >= 5;

    const handleClose = () => {
        setIsOpen(false);
        localStorage.setItem('studio_jenial_onboarding_seen_v2', 'true');
    };

    const handleNext = () => {
        setTargetRect(null); // Clear rectangle immediately to avoid stale position
        if (currentStep < steps.length - 1) {
            setCurrentStep(c => c + 1);
        } else {
            handleClose();
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(c => c - 1);
        }
    };

    const handleSkip = () => {
        handleClose();
    };

    if (!isOpen || isModalConflict) return null;

    const step = steps[currentStep];

    // RENDER: CENTERED MODAL (If no target or target not found)
    if (!step.targetId || !targetRect) {
        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4 animate-in fade-in duration-300">
                <div className="bg-[#111] border border-gray-800 rounded-2xl max-w-2xl w-full shadow-2xl relative overflow-hidden flex flex-col md:flex-row min-h-[400px]">
                    <div className={`w-full md:w-2/5 ${step.color} p-8 flex flex-col items-center justify-center text-white relative overflow-hidden`}>
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20"></div>
                        <div className="relative z-10 transform scale-150 mb-6 drop-shadow-lg">
                            {step.icon}
                        </div>
                    </div>

                    <div className="w-full md:w-3/5 p-8 flex flex-col relative">
                        <button onClick={handleClose} className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors">
                            <XIcon className="w-6 h-6" />
                        </button>
                        <div className="flex-1 flex flex-col justify-center">
                            <span className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                                Guide de démarrage • Étape {currentStep + 1}/{steps.length}
                            </span>
                            <h2 className="text-2xl font-bold text-white mb-4">
                                {step.title}
                            </h2>
                            <p className="text-gray-400 text-lg leading-relaxed">
                                {step.description}
                            </p>
                        </div>
                        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-800">
                            <div className="flex gap-2">
                                {steps.map((_, idx) => (
                                    <div key={idx} className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentStep ? 'bg-white w-6' : 'bg-gray-700'}`} />
                                ))}
                            </div>
                            <button
                                onClick={handleNext}
                                className="px-6 py-2 bg-white text-black font-bold rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                            >
                                {currentStep === 0 ? "Commencer" : "Étape suivante"}
                                <ChevronRightIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // RENDER: INTERACTIVE OVERLAY (If target found)
    const getTooltipPosition = () => {
        if (!targetRect) return { top: 0, left: 0, transform: '' };

        const space = padding + 16;
        const margin = 16;
        const tooltipWidth = 340;
        // Estimate height or use a safe max (content varies)
        const estimatedHeight = 300;

        let finalPlacement = step.placement || 'bottom';
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // --- 1. Auto-Flip Logic ---
        // Basic check: if preferred placement pushes tooltip off-screen, try opposite

        // Check Bottom vs Top
        if (finalPlacement === 'bottom') {
            if (targetRect.bottom + space + estimatedHeight > viewportHeight - margin) {
                // Not enough space below, force to top
                finalPlacement = 'top';
            }
        } else if (finalPlacement === 'top') {
            if (targetRect.top - space - estimatedHeight < margin) {
                // Not enough space above, force to bottom
                finalPlacement = 'bottom';
            }
        }

        // Check Right vs Left
        if (finalPlacement === 'right') {
            if (targetRect.right + space + tooltipWidth > viewportWidth - margin) {
                finalPlacement = 'left';
            }
        } else if (finalPlacement === 'left') {
            if (targetRect.left - space - tooltipWidth < margin) {
                finalPlacement = 'right';
            }
        }

        // --- 2. Calculate Coords based on (potentially flipped) placement ---
        let top = 0;
        let left = 0;
        let transform = '';

        switch (finalPlacement) {
            case 'bottom':
                top = targetRect.bottom + space;
                left = targetRect.left + (targetRect.width / 2);
                transform = 'translate(-50%, 0)';
                break;
            case 'top':
                top = targetRect.top - space;
                left = targetRect.left + (targetRect.width / 2);
                transform = 'translate(-50%, -100%)';
                break;
            case 'left':
                top = targetRect.top + (targetRect.height / 2);
                left = targetRect.left - space;
                transform = 'translate(-100%, -50%)';
                break;
            case 'right':
                top = targetRect.top + (targetRect.height / 2);
                left = targetRect.right + space;
                transform = 'translate(0%, -50%)';
                break;
            default:
                // Fallback to center-ish if something is weird
                top = viewportHeight / 2;
                left = viewportWidth / 2;
                transform = 'translate(-50%, -50%)';
        }

        // --- 3. Viewport Clamping (Secondary safety) ---
        // Ensure "left" doesn't push the tooltip off horizontal edges
        // We need to account for the 'transform' which shifts the element relative to 'left'

        // For Top/Bottom, we are centered (-50%), so real edges are left +/- width/2
        if (finalPlacement === 'top' || finalPlacement === 'bottom') {
            const halfWidth = tooltipWidth / 2;
            // Left Edge
            if (left - halfWidth < margin) {
                left = margin + halfWidth;
            }
            // Right Edge
            if (left + halfWidth > viewportWidth - margin) {
                left = viewportWidth - margin - halfWidth;
            }
        }

        // For Left/Right, we might want to clamp vertical if element is taller than screen (unlikely but safe)
        if (finalPlacement === 'left' || finalPlacement === 'right') {
            const halfHeight = estimatedHeight / 2; // rough calc since we center vertically
            if (top - halfHeight < margin) top = margin + halfHeight;
            if (top + halfHeight > viewportHeight - margin) top = viewportHeight - margin - halfHeight;
        }

        // Update arrow rendering logic below to match this dynamic 'finalPlacement'
        // We can't easily pass 'finalPlacement' to the render return unless we store it or use a ref logic, 
        // but for now, the Arrow is rendered based on 'step.placement'. 
        // FIX: We need to override the arrow rendering too. 
        // Since we are inside the render function's scope, we can't easily change the step object.
        // We will misuse the 'transform' to help align, or just accept that the arrow might point slightly wrong if we flipped.
        // BETTER FIX: We should probably recalculate the arrow class based on the chosen placement.

        // To fix the arrow, we will just return the placement too, but the types assume this function returns {top, left, transform}.
        // Let's attach the placement to the returned object if we can, or just execute the arrow logic inline here?
        // No, 'getTooltipPosition' is called inside render. We can just move the placement logic out?
        // Actually, let's just make getTooltipPosition return the placement_used.

        return { top, left, transform, effectivePlacement: finalPlacement };
    };

    const { top, left, transform, effectivePlacement } = getTooltipPosition();

    return (
        <>
            {/* Dark Overlay with "Hole" created by box-shadow trick */}
            <div
                className="fixed z-[9998] rounded-xl transition-all duration-500 ease-in-out pointer-events-none border-4 border-indigo-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.65),0_0_30px_rgba(129,140,248,0.5)]"
                style={{
                    top: targetRect.top - padding,
                    left: targetRect.left - padding,
                    width: targetRect.width + (padding * 2),
                    height: targetRect.height + (padding * 2),
                }}
            />

            {/* 2. Tooltip Card */}
            <div
                className="fixed z-[9999] text-left transition-all duration-500 ease-out pointer-events-none"
                style={{ top, left, transform }}
            >
                <div className="bg-[#1a1a1a] border border-gray-700 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-0 w-[340px] relative pointer-events-auto overflow-hidden animate-in zoom-in-95 fade-in duration-300">
                    <div className={`${step.color} h-2 w-full`} />

                    <div className="p-6">
                        {/* Arrow */}
                        <div
                            className={`absolute w-4 h-4 bg-[#1a1a1a] border-gray-700 rotate-45 transform 
                                ${effectivePlacement === 'bottom' ? '-top-2 left-1/2 -translate-x-1/2 border-t border-l' : ''}
                                ${effectivePlacement === 'top' ? '-bottom-2 left-1/2 -translate-x-1/2 border-b border-r' : ''}
                                ${effectivePlacement === 'right' ? '-left-2 top-1/2 -translate-y-1/2 border-b border-l' : ''}
                                ${effectivePlacement === 'left' ? '-right-2 top-1/2 -translate-y-1/2 border-t border-r' : ''}
                            `}
                        />

                        <div className="flex items-center gap-3 mb-4">
                            <div className={`p-2 rounded-lg ${step.color} bg-opacity-20 text-white shrink-0`}>
                                {React.cloneElement(step.icon as React.ReactElement<{ className?: string }>, { className: "w-6 h-6" })}
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-bold text-white text-base truncate leading-tight">{step.title}</h3>
                                <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-0.5">Étape {currentStep + 1} / {steps.length}</div>
                            </div>
                        </div>

                        <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                            {step.description}
                        </p>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                            <button onClick={handleSkip} className="text-xs text-gray-500 hover:text-white transition-colors px-2">
                                Passer
                            </button>

                            <div className="flex gap-2">
                                {currentStep > 0 && (
                                    <button onClick={handlePrev} className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors border border-gray-700">
                                        <ChevronLeftIcon className="w-4 h-4" />
                                    </button>
                                )}
                                <button
                                    onClick={handleNext}
                                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg transition-all flex items-center gap-2 shadow-lg shadow-indigo-600/20"
                                >
                                    {currentStep === steps.length - 1 ? 'Terminer' : 'Suivant'}
                                    <ChevronRightIcon className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
